import { kebabCase, map, startCase } from 'lodash';

import { Block } from '@/components/block';
import { Heading } from '@/components/heading';
import { StatusItem } from '@/components/status-item';
import { PortDataInterface } from '@/types';
import {
  OceanCargoInfoApiInterface,
  OceanVesselGroupApiInterface,
  OceanVesselInfoApiInterface,
} from '@/types/api-types';
import { BlockBody, BlockHeading, BlockId } from '@/types/block-types';
import { formatPortAccessFeatureSet } from '@/utils/format';
import { tv } from '@/utils/styles';

export interface FeaturesStatusBlockProps {
  id: BlockId;
  heading?: BlockHeading;
  body?: BlockBody;
  data?: PortDataInterface['seaportInfo'];
  noTopPadding?: boolean;
}

const FeaturesStatusBlock = ({ id, heading, body, data, noTopPadding = false, ...props }: FeaturesStatusBlockProps) => {
  const { sectionStyles, grid, ul, li, nestedUl, nestedLi } = styles();

  return (
    <Block id={id} heading={heading} body={body} noTopPadding={noTopPadding} {...props}>
      <div className={grid()}>
        {map(data, (section, key) => {
          if (key === 'featureSet') {
            return (
              <div className={sectionStyles()} key={`${id}-${key}`} data-cy="features-status-block-section">
                <Heading variant="h4" as="h3" className="mb-6">
                  Port Access
                </Heading>
                <ul className={ul()}>
                  {data
                    ? formatPortAccessFeatureSet(data.featureSet).map((featureSetitem) => {
                        return (
                          <li className={li()} key={`${id}-port-access-${featureSetitem.label}`}>
                            <StatusItem
                              icon={featureSetitem.icon}
                              isDisabled={!featureSetitem.available}
                              className="lg:!max-w-none"
                            >
                              {featureSetitem.label}
                            </StatusItem>
                          </li>
                        );
                      })
                    : null}
                </ul>
              </div>
            );
          }

          return (
            <div
              className={sectionStyles({ className: 'max-md:pt-6 md:pl-6 lg:pl-8' })}
              key={`${id}-${key}`}
              data-cy="features-status-block-section"
            >
              <Heading variant="h4" as="h3" className="mb-6">
                {startCase(key)}
              </Heading>
              <ul className={ul()}>
                {map(section, (item: OceanVesselGroupApiInterface | OceanCargoInfoApiInterface) => {
                  return (
                    <li className={li()} key={`${id}-${key}-${kebabCase(item.label)}`}>
                      <StatusItem isDisabled={!item.available}>{item.label}</StatusItem>
                      {'vessels' in item && item.vessels && (
                        <ul className={nestedUl()}>
                          {item.vessels.map((childItem: OceanVesselInfoApiInterface) => {
                            return (
                              <li className={nestedLi()} key={`${id}-${key}-${kebabCase(childItem.label)}`}>
                                <StatusItem isDisabled={!childItem.available} size="sm">
                                  {childItem.shortLabel || childItem.label}
                                </StatusItem>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </Block>
  );
};

const styles = tv({
  slots: {
    sectionStyles: 'relative border-b-2 border-grey-200 pb-6 md:border-b-0 md:pb-0 md:pr-6 lg:pr-8',
    grid: 'grid grid-cols-1 md:grid-cols-3 md:divide-x-2 md:divide-solid md:divide-grey-200 lg:grid-cols-[1fr_2fr_2fr]',
    ul: 'relative columns-[8rem] gap-x-8 xl:columns-[10rem]',
    li: 'mb-3 break-inside-avoid last:mb-0',
    nestedUl: 'my-4 border-l border-grey-200 pl-4',
    nestedLi: 'mb-2 last:mb-0',
  },
});

export { FeaturesStatusBlock };
